import { FC, useCallback } from "react";
import { FormattedDate, FormattedMessage, useIntl } from "react-intl";

import { ArrowBack as ArrowBackIcon } from "@mui/icons-material";
import {
  AppBar,
  CircularProgress,
  Dialog,
  Grid,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Toolbar,
  Typography,
} from "@mui/material";
import BackdropPrimaryMain from "components/BackdropPrimaryMain";
import { useAppDispatch, useAppSelector } from "store";
import {
  handleCloseAddChecklist,
  selectSelectedJob,
  setAddChecklistSelectOpen,
} from "store/slices/jobs.store";
import { AddChecklistSelectDialog } from "./AddChecklistSelectDialog";

export const AddChecklistDialog: FC = () => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const job = useAppSelector(selectSelectedJob);
  const currentMachine = job?.machine;
  const machineInstallDate = currentMachine?.installDate;
  const { open, loading } = useAppSelector((state) => state.jobs.addChecklist);

  const handleClose = useCallback(() => {
    dispatch(handleCloseAddChecklist());
  }, [dispatch]);

  return (
    <Dialog fullScreen open={open} onClose={handleClose} data-testid="AddChecklistDialog">
      <BackdropPrimaryMain open={loading}>
        <CircularProgress color="inherit" />
      </BackdropPrimaryMain>
      <AppBar position="fixed">
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={handleClose}
            aria-label={intl.formatMessage({ id: "general.back" })}
            size="large"
            data-testid="AddChecklistDialog-CloseButton"
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" marginLeft="16px" flex={1}>
            {`${job?.externalId} `} / <FormattedMessage id="visit.addChecklist" />
          </Typography>
        </Toolbar>
      </AppBar>
      <Toolbar />
      <List>
        <ListItemButton
          divider
          data-testid="AddChecklist-WithoutMachine"
          onClick={() => dispatch(setAddChecklistSelectOpen({ open: true, useMachine: false }))}
        >
          <ListItemText>
            <FormattedMessage id="visit.addChecklistWithoutMachine" />
          </ListItemText>
        </ListItemButton>

        {currentMachine && (
          <ListItemButton
            divider
            component={Grid}
            onClick={() => dispatch(setAddChecklistSelectOpen({ open: true, useMachine: true }))}
          >
            <Grid item>
              <Typography fontWeight={700}>{currentMachine.modelDescription}</Typography>
              <Typography>{currentMachine.machineNo}</Typography>
              {currentMachine.idNo && <Typography>{currentMachine.idNo}</Typography>}
              {currentMachine.location && <Typography>{currentMachine.location}</Typography>}
              {machineInstallDate && (
                <Typography>
                  {`${intl.formatMessage({ id: "general.installed" })} `}
                  <FormattedDate
                    value={machineInstallDate}
                    day="numeric"
                    month="long"
                    year="numeric"
                  />
                </Typography>
              )}
            </Grid>
          </ListItemButton>
        )}
      </List>
      <AddChecklistSelectDialog />
    </Dialog>
  );
};
